import { useState, useEffect } from 'react';
import DateTimePicker from 'react-datetime';
import moment from "moment";

import "react-datetime/css/react-datetime.css";
import './styles.css';
import Alert from '../../../alert';
import { useTour } from '../../../../../context/TourContext';

import { gameOptions } from './data';

export default function InstructorAssignmentForm({
  onSave,
  games = [],
  gamesLimit = 5,
  isButtonLoading,
  initialValues
}) {
  const { isActive: isTourActive, currentStep } = useTour();
  const isTourSelectStep = isTourActive && currentStep?.targetId === 'select-activity';

  const [isFormChanged, setIsFormChanged] = useState(false);
  const [name, setName] = useState(initialValues?.name || '');

  useEffect(() => {
    if(!initialValues) {
      if(games?.length) {
        setName(`Assignment #${games.length + 1}`);
      } else {
        setName(`Assignment #1`);
      }
    }
  }, [games, initialValues]);

  const [description, setDescription] = useState(initialValues?.description || '');
  const [dateFrom, setDateFrom] = useState(initialValues ? '' : moment().add(1, 'days'));
  const [dateTo, setDateTo] = useState(initialValues ? '' : moment().add(30, 'days'));
  const [selectedAssignment, setSelectedAssignment] = useState(initialValues?.assignmentType || null);

  const [isDateFromChanged, setIsDateFromChanged] = useState(false);
  const [isDateToChanged, setIsDateToChanged] = useState(false);

  useEffect(() => {
    if (initialValues?.name) {
      setName(initialValues.name);
    }
    if (initialValues?.description) {
      setDescription(initialValues.description);
    }
    if (initialValues?.startTime) {
      setDateFrom(moment(initialValues.startTime));
    }
    if (initialValues?.endTime) {
      setDateTo(moment(initialValues.endTime));
    }
    if (initialValues?.assignmentType) {
      setSelectedAssignment(initialValues.assignmentType);
    }
  }, [initialValues]);
  
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertText, setAlertText] = useState('');

  const handleGameButtonClick = (game) => {
    setSelectedAssignment(game);
    setIsFormChanged(true);
    if(game === 'Account Classification') {
      setDescription('In this exercise, accounts are falling and you have to classify them quickly and accurately to build mastery.');
    } else if(game === 'Normal Balance') {
      setDescription('In this exercise, you have to sort accounts to their correct normal balances.');      
    } else if(game === 'Balanced Equation') {
      setDescription('In this exercise, you\'ll review transactions and determine their impact on the accounting equation. The decisions may include increases/decreases, debits and credits, specific accounts, and/or income statement impact.');
    } else if(game === 'The Principles') {
      setDescription('In this exercise, you\'ll analyze a series of activities and identify which principles, assumptions, and constraints apply to each.');
    }
  }
  const handleNameChange = (e) => {
    setName(e.target.value);

    if(selectedAssignment) {
      setIsFormChanged(true);
    }
  };
  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);

    if(selectedAssignment) {
      setIsFormChanged(true);
    }
  };
  
  const handleDateFromChange = (date) => {
    setIsDateFromChanged(true);

    const newDateFrom = moment(date);
  
    if (selectedAssignment) {
      setIsFormChanged(true);
    }
  
    setDateFrom(newDateFrom);
  };
  

  const handleDateToChange = (date) => {
    setIsDateToChanged(true);

    const selectedDate = moment(date);

    if(selectedAssignment) {
      setIsFormChanged(true);
    }
  
    setDateTo(selectedDate);
  };
  

  const handleConfirmAlertButtonClick = () => {
    setIsAlertVisible(false);
  }

  const handleSaveButtonClick = () => {
    if(!name) {
      setAlertTitle('Error | Name can\'t be blank');
      setAlertText('The assignment name is required. Please enter a valid name before proceeding.');
      setIsAlertVisible(true);
      return;
    }

    if(initialValues?.assignmentType !== 'Tournament') {
      if (!dateFrom.isValid()) {
        setAlertTitle('Error | Not valid date');
        setAlertText('Invalid date selected for From date.');
        setIsAlertVisible(true);
        return;
      }

      if (dateFrom.startOf('minute').isBefore(moment().startOf('minute')) && isDateFromChanged) {
        setAlertTitle('Error | In the Past');
        setAlertText('The start time cannot be earlier than the current time.');
        setIsAlertVisible(true);
        return;
      }
    
      if (dateFrom.isSameOrAfter(dateTo)) {
        setAlertTitle('Error | Start Time > End Time');
        setAlertText('The start time must be earlier than the end time.');
        setIsAlertVisible(true);
        // setDateFrom(moment().endOf('day').add(1, 'days'));
        return;
      }

      if (!dateTo.isValid()) {
        console.warn('Invalid date selected for end time');
        return;
      }
    
      if (dateTo.isSameOrBefore(moment()) && isDateToChanged) {
        setAlertTitle('Error | End Time < Current Time');
        setAlertText('The end time must be later than the current time.');
        setIsAlertVisible(true);
        // setDateTo(moment().add(30, 'days'));
        return;
      }
    
      if (dateTo.isSameOrBefore(dateFrom)) {
        setAlertTitle('Error | Start Time > End Time');
        setAlertText('The start time must be earlier than the end time.');
        setIsAlertVisible(true);
        // setDateTo(moment().add(30, 'days'));
        return;
      }
    }

    onSave({
      name,
      description,
      startTime: dateFrom,
      endTime: dateTo,
      gameType: selectedAssignment,
      formSavedCallback: () => setIsFormChanged(false)
    });
  }


  const effectiveAssignmentType = initialValues?.assignmentType ?? selectedAssignment;
  const showNonTournamentFields = typeof effectiveAssignmentType !== 'undefined' && effectiveAssignmentType !== 'Tournament';

  const ActivitySelector = showNonTournamentFields && (
    <>
      <div className="instructor-input-container">
        <label className="full-width">Select an Activity</label>

        <div className="assignment-game-buttons" data-tour-id="select-activity">
          {gameOptions.map((gameOption, index) => {
            const gameTypeCount = games.filter(game => game.game_type === gameOption.name).length;
            const isTourRestricted = isTourSelectStep && gameOption.name !== 'Account Classification';
            const isButtonDisabled = !gameOption.active || initialValues || gameTypeCount >= gamesLimit || isTourRestricted;

            let title = '';
            if (isTourRestricted) {
              title = 'Select Classification for this walkthrough';
            } else if (gameTypeCount >= gamesLimit) {
              title = 'Limit reached! You\'ve reached the maximum number of games for this type.';
            }

            return (
              <button
                key={index}
                onClick={() => handleGameButtonClick(gameOption.name)}
                className={`button-game ${selectedAssignment === gameOption.name ? 'active' : ''} ${isTourRestricted ? 'tour-restricted' : ''}`}
                disabled={isButtonDisabled}
                title={title}
              >
                {gameOption.name}
              </button>
            );
          })}
        </div>
      </div>

      <hr />
    </>
  );

  const SubmissionFields = showNonTournamentFields && (
    <>
      <div className="instructor-input-container">
        <label htmlFor="assignment-description">Description</label>
        <textarea 
          id="assignment-description"
          value={description}
          onChange={handleDescriptionChange}
        />
      </div>

      <div className="instructor-input-container">
        <label>Submission Window</label>

        <div className="instructor-date-selector-wrapper date-selector-from">
          <label htmlFor="assignment-from" className="instructor-date-selector-label">From</label>
          <DateTimePicker
            dateFormat="ddd, MMM DD YYYY,"
            timeFormat="hh:mm a"
            inputProps={{
              id: "assignment-from",
              className: "instructor-datetime-selector"
            }}
            value={dateFrom}
            onChange={handleDateFromChange}
          />
        </div>

        <div className="instructor-date-selector-wrapper date-selector-to">
          <label htmlFor="assignment-to" className="instructor-date-selector-label">To</label>
          <DateTimePicker
            dateFormat="ddd, MMM DD YYYY,"
            timeFormat="hh:mm a"
            inputProps={{
              id: "assignment-to",
              className: "instructor-datetime-selector"
            }}
            value={dateTo}
            onChange={handleDateToChange}
          />
        </div>

        <button 
          className={`button-blue button-save-assignment ${isButtonLoading ? 'loading' : ''}`}
          disabled={!isFormChanged}
          onClick={handleSaveButtonClick}
        >
          {!isButtonLoading && 'Save'}
        </button>
      </div>
    </>
  );

  const TournamentSave = initialValues?.assignmentType === 'Tournament' && (
    <div className="instructor-input-container">
      <button 
        className={`button-blue button-save-assignment ${isButtonLoading ? 'loading' : ''}`}
        disabled={!isFormChanged}
        onClick={handleSaveButtonClick}
      >
        {!isButtonLoading && 'Save'}
      </button>
    </div>
  );

  if (initialValues && typeof initialValues.assignmentType === 'undefined') {
    return null;
  }

  return (
    <div className="instructor-add-assignment-form">
      {isAlertVisible && (
        <Alert 
          type="error"
          title={alertTitle}
          text={alertText}
          confirmButtonText="Done"
          onConfirmButtonClick={handleConfirmAlertButtonClick}
        />
      )}
      <hr />
      {ActivitySelector}
      {selectedAssignment && (
        <div data-tour-id="general-info-panel">
          <div className="instructor-input-container">
            <label htmlFor="assignment-name">Name</label>
            <div className="instructor-input-with-counter">
              <input
                id="assignment-name"
                type="text"
                value={name}
                onChange={handleNameChange}
                maxLength={50}
              />
              <span className="instructor-input-counter" aria-live="polite">
                {name.length}/50
              </span>
            </div>
          </div>
          {SubmissionFields}
          {TournamentSave}
          <hr />
        </div>
      )}

    </div>
  );
}
